import React, { useState, useMemo, useEffect, useRef } from "react";
import { ChevronUp, ChevronDown, Info } from "lucide-react";
import { useTableColumnResize } from "../hooks/useTableColumnResize";
import "./OfficeAdminTable.css";

const TakeoffManagementTable = ({
  takeoffs,
  clients = [],
  onRowClick,
  showColumnMenu,
  setShowColumnMenu,
  onTakeoffUpdate,
  onAutoSizeColumns,
}) => {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [visibleColumns] = useState(() => {
    const saved = localStorage.getItem("takeoff-management-columns");
    return saved
      ? JSON.parse(saved)
      : {
          action: true,
          projectName: true,
          jobNumber: true,
          status: true,
          abCivilPrice: true,
          ufredoPrice: true,
          formClient: true,
          invoicedStatus: true,
          invoicedDate: true,
          paidDate: true,
          earthworksTechInvNumber: true,
          poNumber: true,
          paidToUfredo: true,
          paidToUfredoDate: true,
        };
  });

  const columns = [
    { key: "action", label: "", sortable: false, width: "60px" },
    { key: "projectName", label: "Projects", sortable: true, width: "250px" },
    { key: "jobNumber", label: "Job Number", sortable: true, width: "120px" },
    { key: "status", label: "Status", sortable: true, width: "130px" },
    { key: "abCivilPrice", label: "AB Civil Price", sortable: true, width: "140px" },
    { key: "ufredoPrice", label: "Ufredo Price", sortable: true, width: "130px" },
    { key: "formClient", label: "Client", sortable: true, width: "150px" },
    { key: "invoicedStatus", label: "Invoiced Status", sortable: true, width: "140px" },
    { key: "invoicedDate", label: "Invoiced Date", sortable: true, width: "120px" },
    { key: "paidDate", label: "Paid Date", sortable: true, width: "120px" },
    { key: "earthworksTechInvNumber", label: "Earthworks Tech Inv Number", sortable: true, width: "200px" },
    { key: "poNumber", label: "PO Number", sortable: true, width: "140px" },
    { key: "paidToUfredo", label: "Paid to Ufredo", sortable: true, width: "130px" },
    { key: "paidToUfredoDate", label: "Paid to Ufredo Date", sortable: true, width: "150px" },
  ];

  // Table ref for resize functionality
  const tableRef = useRef(null);

  // Initialize column resize hook
  const {
    columnWidths,
    initializeResize,
    autoSizeColumns
  } = useTableColumnResize('takeoff-management-table', columns);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Initialize resize functionality
  useEffect(() => {
    if (tableRef.current) {
      const cleanup = initializeResize(tableRef.current);
      return cleanup;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pass autoSizeColumns to parent
  useEffect(() => {
    if (onAutoSizeColumns) {
      onAutoSizeColumns(autoSizeColumns);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSizeColumns]);

  const sortedTakeoffs = useMemo(() => {
    if (!sortField) return takeoffs;

    return [...takeoffs].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      // Handle numeric values
      if (sortField === "ufredoPrice" || sortField === "abCivilPrice") {
        // Parse price values
        aValue = parseFloat(String(aValue).replace(/[^0-9.-]/g, "")) || 0;
        bValue = parseFloat(String(bValue).replace(/[^0-9.-]/g, "")) || 0;
      }

      // Handle dates
      if (
        ["invoicedDate", "paidDate", "paidToUfredoDate"].includes(sortField)
      ) {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Perform comparison
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [takeoffs, sortField, sortDirection]);

  const formatPrice = (price) => {
    if (!price && price !== 0) return "";
    // Remove any existing $ sign and parse
    const numericPrice = parseFloat(String(price).replace(/[^0-9.-]/g, ""));
    if (isNaN(numericPrice)) return price;
    return `$${numericPrice.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getStatusClass = (status, type) => {
    if (!status) return "";

    if (type === "status") {
      if (status === "Completed") return "status-success";
      if (status === "In Progress") return "status-info";
      if (status === "On Hold") return "status-warning";
    }

    if (type === "invoiced") {
      if (status === "Done" || status === "Paid") return "status-success";
      if (status === "Pending") return "status-warning";
    }

    if (type === "paidToUfredo") {
      if (status === "Paid" || status === "Yes") return "status-success";
      if (status === "Pending") return "status-warning";
      if (status === "No") return "status-error";
    }

    return "";
  };

  const renderCellContent = (takeoff, column) => {
    const value = takeoff[column.key];

    switch (column.key) {
      case "action":
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRowClick(takeoff);
            }}
            style={{
              padding: '8px',
              background: 'none',
              border: 'none',
              color: 'var(--gray-400)',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--gray-800)';
              e.currentTarget.style.color = 'var(--cta-blue)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--gray-400)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="View Details"
          >
            <Info size={20} />
          </button>
        );

      case "projectName":
        return (
          <span style={{ fontWeight: '500' }}>
            {value || '-'}
          </span>
        );

      case "jobNumber":
        return value || '-';

      case "abCivilPrice":
      case "ufredoPrice":
        return formatPrice(value);

      case "invoicedDate":
      case "paidDate":
      case "paidToUfredoDate":
        return formatDate(value);

      case "status":
        return value ? (
          <span className={`status-pill ${getStatusClass(value, "status")}`}>
            {value}
          </span>
        ) : (
          "-"
        );

      case "invoicedStatus":
        return (
          <span className={`status-pill ${getStatusClass(value || '', "invoiced")}`}>
            {value || "Not Set"}
          </span>
        );

      case "paidToUfredo":
        return value ? (
          <span
            className={`status-pill ${getStatusClass(value, "paidToUfredo")}`}
          >
            {value}
          </span>
        ) : (
          "-"
        );

      default:
        return value || "-";
    }
  };

  return (
    <div className="table-scroll-container">
      <table className="office-admin-table" ref={tableRef}>
        <thead>
          <tr>
            {columns.map((column) =>
              visibleColumns[column.key] ? (
                <th
                  key={column.key}
                  className={column.sortable ? "sortable" : ""}
                  style={{ width: columnWidths[column.key] || column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="header-content">
                    <span>{column.label}</span>
                    {column.sortable && sortField === column.key && (
                      <span className="sort-icon">
                        {sortDirection === "asc" ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ) : null,
            )}
          </tr>
        </thead>
        <tbody>
          {sortedTakeoffs.length === 0 ? (
            <tr>
              <td
                colSpan={Object.values(visibleColumns).filter(Boolean).length}
                className="empty-row"
              >
                No takeoff projects found
              </td>
            </tr>
          ) : (
            sortedTakeoffs.map((takeoff) => (
              <tr
                key={takeoff.id}
                className="table-row clickable-row"
                onClick={() => onRowClick(takeoff)}
                style={{ cursor: 'pointer' }}
              >
                {columns.map((column) =>
                  visibleColumns[column.key] ? (
                    <td key={column.key}>
                      {renderCellContent(takeoff, column)}
                    </td>
                  ) : null,
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TakeoffManagementTable;
